export const PDF_TEXTS = {
  sidebar: {
    title: 'Configuration',
    timeline: {
      title: 'Audit Timeline',
      current: 'Current Audit',
      first: 'First Audit'
    },
    generationMode: {
      title: 'Generation Mode',
      manual: 'Manual',
      automatic: 'Automatic'
    },
    frequency: {
      label: 'Frequency',
      week: 'Weekly',
      month: 'Monthly'
    },
    days: {
      monday: 'Monday',
      tuesday: 'Tuesday',
      wednesday: 'Wednesday',
      thursday: 'Thursday',
      friday: 'Friday',
      saturday: 'Saturday',
      sunday: 'Sunday'
    },
    sections: {
      title: 'Sections to Include',
      includeAllKeywords: 'Include all keywords',
      options: {
        max_limit: 'All sections selected' // Optional fallback
      }
    },
    recipients: {
      title: 'Recipients',
      helperText: 'Send reports to specific email (Max 5).',
      label: 'Email Addresses',
      placeholder: 'Press Enter to add',
      available: 'email available',
      errors: {
        maxExceeded: 'Maximum 5 recipients allowed.',
        invalidEmail: 'Invalid email format'
      }
    },
    customIcon: {
      title: 'Custom Icon',
      addIcon: 'Add Icon',
      alt: 'Selected Icon'
    },
    aiSummary: {
      title: 'AI Summary',
      format: 'Summary format',
      goals: 'Goals of report',
      tone: 'Tone of voice',
      examplePreview: 'Example preview',
      options: {
        short: 'Short',
        bullet: 'Bullet List',
        detailed: 'Detailed',
        formal: 'Formal',
        professional: 'Professional',
        neutral: 'Neutral',
        noSections: 'No sections available'
      },
      validation: {
        minGoals: 'Please select at least one goal to continue.'
      }
    },
    footer: {
      reset: 'Reset changes',
      save: 'Save changes',
      updating: 'Updating...'
    }
  },
  preview: {
    title: 'Preview',
    downloadButton: 'Download Report',
    placeholder: 'Preview will be available here',
    viewport: {
      failedTitle: 'Failed to Load PDF',
      failedMessage:
        'The PDF data could not be rendered. Please try refreshing.',
      noContent: 'No PDF content to display.',
      defaultTitle: 'PDF Preview'
    },
    canvas: {
      siteAuditOverview: 'Site Audit Overview',
      seoPerformanceOverview: 'SEO Performance Overview',
      comparedWithFirst: 'Compared with first audit',
      aiSummaryTitle: 'AI SEO Summary',
      generatedBy: 'Generated by AI Insights Engine',
      aiSummaryPlaceholderTitle: 'AI Summary',
      aiSummaryPlaceholderText:
        'All set—once your report is generated, AI will analyze it and automatically add a summary to the final version.',
      aiSummaryFallback:
        '"AI Summary will be generated here based on your report configuration."',
      showPreview: 'Show preview',
      hidePreview: 'Hide preview',
      refreshSummary: 'Refresh Summary',
      refreshing: 'Refreshing...',
      dummySummary: `**SEO Performance Overview**\nYour website's organic visibility has shown a strong upward trend over the last month, with a notable **15% increase** in keyword rankings across major search engines. The technical health score remains stable at **92/100**, indicating a well-optimized foundation.\n\n**Key Insights & Opportunities**\n- **Mobile Optimization:** Recent updates to the mobile site have improved average load times by **1.2 seconds**, positively impacting bounce rates.\n- **Content Gap:** There is a significant opportunity to capture more traffic by targeting long-tail keywords identified in the "Keyword Tracking" section.\n- **Technical Debt:** Addressing the remaining **24 broken links** and optimizing meta descriptions for the "Scanned Pages" will further enhance search engine crawlability.\n\n**Strategic Recommendations**\nFocus on high-impact SEO tasks identified for this period, particularly those related to **E-E-A-T signals** and local search optimization. Continued monitoring of AI visibility trends is recommended as search engine algorithms evolve.`
    },
    noSections: {
      title: 'No Sections Selected',
      message:
        'Please select at least one section from the sidebar to include in your PDF report.'
    },
    loading: 'Generating Preview...',
    loadingReport: 'Loading Report Data...',
    updatingPreview: 'Generating your report preview...',
    savingConfig: 'Saving your changes...',
    thisMayTakeAFewSeconds: 'This may take a few seconds.',
    error: {
      title: 'Failed to load report data',
      defaultMessage:
        'An error occurred while fetching the necessary data for the PDF.',
      retry: 'Retry',
      backToHome: 'Back to Home'
    },
    modal: {
      title: 'Generate Report',
      dashboardToggle: 'Create online dashboard',
      emailToggle: 'Email PDF report',
      emailPlaceholder: 'To...',
      fileNameDefault: 'PDF Export',
      fileNamePlaceholder: 'Filename or Subject',
      recentMessages: 'Recent messages',
      editorPlaceholder: 'Enter your message',
      signatureLabel: 'Your signature',
      customization: {
        title: 'Email address customization',
        usernamePlaceholder: 'Username',
        domain: '@myreports.email',
        replyToPlaceholder: 'Reply-to email',
        senderNamePlaceholder: "Sender's name"
      },
      schedule: 'Schedule report',
      generateBtn: 'Generate',
      generateBtnSend: 'Generate & Send',
      generateBtnSave: 'Generate & Save',
      saveChanges: 'Save Changes',
      downloadReport: 'Download Report',
      sendReport: 'Send Report',
      updating: 'Updating...',
      downloading: 'Downloading...',
      sending: 'Sending...',
      validation: {
        recipientRequired: 'At least one email recipient is required',
        duplicateEmail: 'Duplicate email addresses are not allowed',
        fileNameLimit: 'File name must be 50 characters or less'
      },
      alerts: {
        sendSuccess: 'Report sent successfully!',
        sendError: 'Failed to send report.',
        sendGenericError: 'Something went wrong while sending.',
        saveSuccess: 'Changes saved successfully!',
        saveError: 'Failed to save changes.',
        saveGenericError: 'Something went wrong while saving.'
      }
    }
  },
  // NavBar texts
  navBar: {
    period: 'Period',
    compareTo: 'Compare to',
    addAiSummary: 'Add AI summary',
    generatePdfReport: 'Generate PDF report',
    bulkActions: {
      editSections: 'Edit sections',
      resetAll: 'Reset all'
    },
    calendar: {
      siteCreationPrefix: 'Site was added on ',
      siteCreationSuffix: '. Data is only available from this date onwards.',
      dataLimitFallback: 'Currently, data can be displayed for no more than a quarter.',
      shortDays: ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'],
      dayPrefix: 'Day '
    }
  },
  // AI Summary Preview
  aiPreview: {
    analyzing: 'Analyzing your report for the best summary...',
    title: 'AI SEO Summary',
    emptyState: 'AI preview will appear here',
    summarySuffix: ' Summary:',
    goalSelectionError: 'Please select at least one goal to continue.',
    sidebarHelper: 'Configure how the AI generates the SEO summary for your report. You can choose the format, focus areas, and tone.'
  },
  // Local SEO
  localSeo: {
    fetchingDetails: 'Fetching Local SEO Details...',
    loadingProfiles: 'Loading Business Profiles...',
    loadingKeywordReport: 'Loading Keyword Report...',
    allKeywords: 'All Keywords',
    selectKeyword: 'Select Keyword',
    tableHeaders: {
      index: '#',
      businessName: 'Business Name',
      reviews: 'Reviews',
      rating: 'Rating',
      visibility: 'Visibility',
      arp: 'ARP'
    },
    primaryCategory: 'Primary Category:',
    secondaryCategory: 'and Secondary Category:',
    reviewsSuffix: ' Reviews)',
    competitorsSuffix: "'s Top Ranking Competitors",
    keywordPrefix: 'Keyword: '
  },
  // Export Success Modal
  exportSuccess: {
    title: 'Export completed',
    pdfReportFor: 'PDF report for',
    downloadNote: 'Your download will start automatically. If not,',
    downloadLinkText: 'click here to download again',
    shareLabel: 'Share your report',
    linkTab: 'Link',
    embedTab: 'Embed',
    copyLink: 'Copy',
    copied: 'Copied',
    copiedBang: 'Copied!',
    openInNewTab: 'Open in new tab',
    copyEmbedCode: 'Copy embed code'
  },
  common: {
    cancel: 'Cancel',
    apply: 'Apply',
    custom: 'Custom',
    selectGoals: 'Select goals…',
    dataLimitWarning: 'Currently, data can be displayed for no more than a quarter.',
    days: {
      monday: 'Monday',
      tuesday: 'Tuesday',
      wednesday: 'Wednesday',
      thursday: 'Thursday',
      friday: 'Friday',
      saturday: 'Saturday',
      sunday: 'Sunday'
    }
  }
} as const;

export const PERIOD_PRESETS_TEXTS = [
  { value: 'last_7_days', label: 'Past 7 Days' },
  { value: 'last_28_days', label: 'Last 28 Days' },
  { value: 'last_3_months', label: 'Last 3 Months' },
  { value: 'last_12_months', label: 'Last 12 Months' }
];

export const COMPARE_PRESETS_TEXTS = [
  { value: 'last_7_days', label: 'Past 7 Days' },
  { value: 'last_28_days', label: 'Last 28 Days' },
  { value: 'last_3_months', label: 'Last 3 Months' },
  { value: 'last_12_months', label: 'Last 12 Months' }
];

export const SECTION_LABELS: Record<string, string> = {
  ai_summary: 'AI Summary',
  site_overview: 'Site Audit Overview',
  ai_visibility: 'AI Visibility',
  keyword_tracking: 'Keyword Tracking',
  seo_tasks: 'SEO/AIO Tasks',
  scanned_pages: 'Scanned Pages',
  local_seo: 'Local SEO'
};

export const formatSectionLabel = (key?: string): string => {
  if (!key) return '';
  const cleanKey = key.trim().toLowerCase();
  if (SECTION_LABELS[cleanKey]) return SECTION_LABELS[cleanKey];
  
  // Fallback: replace underscores and dashes with spaces, title case
  return key
    .trim()
    .replace(/[_-]/g, ' ')
    .replace(/\b\w/g, (l) => l.toUpperCase());
};

